import React from "react";
import {
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FlightIcon from "@mui/icons-material/Flight";
import RightSwipeableDrawer from "./RightSwipeableDrawer";

const BookingFlightDetails = ({
  fdetails,
  handleFareRules,
  loading,
  airlineLogos,
  durationCalculator,
  fareRuleDetail,
  drawerOpen,
  setDrawerOpen,
}) => {
  return (
    <Paper sx={{ p: 2, borderRadius: "10px" }}>
      {/* Top-Level Flight Details */}
      <Box>
        {fdetails?.Segments?.map((segmentGroup, index) => {
          const source = segmentGroup[0].Origin.Airport.CityName;
          const depTime = segmentGroup[0].Origin.DepTime;
          const destination =
            segmentGroup[segmentGroup.length - 1].Destination.Airport.CityName;
          const totalDuration = durationCalculator(
            segmentGroup[0].Origin.DepTime,
            segmentGroup[segmentGroup.length - 1].Destination.ArrTime
          );

          return (
            <Box key={index}>
              <Box display={"flex"} justifyContent={"space-between"}>
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  gap={2}
                  alignItems={"center"}
                >
                  <Typography fontWeight={300} fontSize={14}>
                    Depart
                  </Typography>
                  <Typography fontWeight={600} fontSize={18}>
                    {source}
                  </Typography>
                  <EastIcon />
                  <Typography fontWeight={600} fontSize={18}>
                    {destination}
                  </Typography>
                </Box>
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  gap={2}
                  alignItems={"center"}
                >
                  <Button
                    onClick={handleFareRules}
                    disabled={loading}
                    sx={{
                      color: "#FF0000",
                      textTransform: "none",
                      borderRadius: "20px",
                    }}
                    endIcon={<ErrorOutlineIcon />}
                  >
                    {loading ? (
                      <CircularProgress size={20} sx={{ color: "#FF0000" }} />
                    ) : (
                      "Fare Rules"
                    )}
                  </Button>
                  <RightSwipeableDrawer
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    onOpen={() => setDrawerOpen(true)}
                    content={fareRuleDetail}
                  />
                </Box>
              </Box>
              <Box display={"flex"} flexDirection={"row"} gap={2}>
                <Typography>
                  {new Date(depTime).toLocaleDateString("en-US", {
                    weekday: "short",
                    day: "2-digit",
                    month: "short",
                    year: "2-digit",
                  })}
                </Typography>
                {" | "}
                <Typography>
                  {segmentGroup.length === 1
                    ? "Direct Flight"
                    : `${segmentGroup.length - 1} stop${
                        segmentGroup.length - 1 > 1 ? "s" : ""
                      }, via ${segmentGroup
                        .slice(0, segmentGroup.length - 1)
                        .map((stop) => stop.Destination.Airport.CityName)
                        .join(", ")}`}
                </Typography>
                {" | "}
                <Typography>{totalDuration}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Individual Segment Details */}
      <Box>
        {fdetails?.Segments?.map((segmentGroup, index) => (
          <Box key={index} mb={3} borderRadius={2}>
            {segmentGroup.map((segment, segIndex) => (
              <Box key={segIndex} mt={2}>
                <Box
                  sx={{
                    background: "#F5F7FA",
                    p: 1,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Box display={"flex"} flexDirection={"row"} gap={2}>
                    <img
                      src={
                        airlineLogos[segment.Airline.AirlineCode] ||
                        "../../Assets/Services/Flight/logos/default.gif"
                      }
                      alt={`${segment.Airline.AirlineName} logo`}
                      style={{
                        width: "45px",
                        height: "45px",
                        borderRadius: "10px",
                      }}
                    />
                    <Box>
                      <Typography>{segment.Airline.AirlineName}</Typography>
                      <Typography>
                        {segment.Airline.AirlineCode} -{" "}
                        {segment.Airline.FlightNumber}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography>Baggage: {segment.Baggage}</Typography>
                    <Typography>
                      Cabin Baggage: {segment.CabinBaggage}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  display={"flex"}
                  justifyContent={"space-between"}
                  px={16}
                  mt={2}
                >
                  <Box textAlign={"right"}>
                    <Typography fontWeight={700} fontSize={"20px"}>
                      {new Date(segment.Origin.DepTime).toLocaleString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </Typography>
                    <Typography fontSize={"18px"}>
                      {new Date(segment.Origin.DepTime).toLocaleString(
                        "en-US",
                        {
                          weekday: "short",
                          day: "2-digit",
                          month: "short",
                          year: "2-digit",
                        }
                      )}
                    </Typography>
                    <Typography fontWeight={300} fontSize={"12px"}>
                      {segment.Origin.Airport.CityName}{" "}
                      <strong>[ {segment.Origin.Airport.AirportCode} ]</strong>
                    </Typography>
                    <Typography fontWeight={300} fontSize={"12px"}>
                      {segment.Origin.Airport.AirportName}
                    </Typography>
                    <Typography fontWeight={300} fontSize={"12px"}>
                      Terminal {segment.Origin.Airport.Terminal || "---"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography color="#027BFE">
                      {`${Math.floor(segment.Duration / 60)} Hr. ${
                        segment.Duration % 60
                      } Mins`}
                    </Typography>
                    <Box display={"flex"} flexDirection={"row"}>
                      <Typography color="#027BFE">- - - - - </Typography>
                      <FlightIcon
                        sx={{ rotate: "90deg", color: "#027BFE", ml: 0.5 }}
                      />
                      <Typography color="#027BFE"> - - - - -</Typography>
                    </Box>
                  </Box>

                  <Box textAlign={"left"}>
                    <Typography fontWeight={700} fontSize={"22px"}>
                      {new Date(segment.Destination.ArrTime).toLocaleString(
                        [],
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        }
                      )}
                    </Typography>
                    <Typography fontSize={"18px"}>
                      {new Date(segment.Destination.ArrTime).toLocaleString(
                        "en-US",
                        {
                          weekday: "short",
                          day: "2-digit",
                          month: "short",
                          year: "2-digit",
                        }
                      )}
                    </Typography>
                    <Typography fontWeight={300} fontSize={"12px"}>
                      {segment.Destination.Airport.CityName}{" "}
                      <strong>
                        [ {segment.Destination.Airport.AirportCode} ]
                      </strong>
                    </Typography>
                    <Typography fontWeight={300} fontSize={"12px"}>
                      {segment.Destination.Airport.AirportName}
                    </Typography>
                    <Typography fontWeight={300} fontSize={"12px"}>
                      Terminal {segment.Destination.Airport.Terminal || "---"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default BookingFlightDetails;
